import {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  useMutation,
} from '@tanstack/react-query';

import { Account } from 'types/account';
import APIError from 'types/api_error';

function isLumeoDomainEmail(email: string | undefined): boolean {
  if (!email) return false;
  return email.endsWith('@lumeo.com') || email.endsWith('@lumeo.dev');
}

import { UpdatePasswordParams } from 'account/components/ChangePasswordForm/ChangePasswordForm';
import { UpdateAccountParams } from 'settings/views/Account/Account';
import { useAuthentication } from 'hooks/api/useAuthentication';
import { useAppQueryClient } from 'hooks/useAppQueryClient';
import { setDefaultDashboard } from 'services/defaultDashboards';

import { useAPI } from './useAPI';
import { useAuthenticatedQuery } from './useAuthenticatedQuery';

export function useAccount({
  enabled,
  ...options
}: UseQueryOptions<Account | undefined, APIError, Account | undefined> = {}) {
  const { user } = useAuthentication();

  const api = useAPI();

  const query = useAuthenticatedQuery<Account | undefined>(
    ['account'],
    () => {
      if (!user?.id) {
        return Promise.reject(new Error('Not authenticated.'));
      }

      return api.accounts.read(user.id);
    },
    {
      ...options,
      enabled:
        enabled !== undefined
          ? enabled && Boolean(user?.id)
          : Boolean(user?.id),
      staleTime: Infinity,
      select(account) {
        if (!account) {
          return account;
        }

        if (
          Array.isArray(account.roles) &&
          account.roles.every((role) => typeof role === 'string')
        ) {
          account.roles = account.roles.map((name) => ({ name })) as any;
        }

        return account;
      },
    }
  );

  return {
    ...query,
    isLumeoDomainAccount: isLumeoDomainEmail(query.data?.email),
  };
}

export function useUpdateAccount({
  onSettled,
  onSuccess,
  ...options
}: UseMutationOptions<
  Account,
  APIError,
  UpdateAccountParams
> = {}): UseMutationResult<Account, APIError, Partial<Account>> {
  const api = useAPI();
  const queryClient = useAppQueryClient();
  const { user } = useAuthentication();

  return useMutation(
    (params) => {
      if (!user) {
        return Promise.reject(new Error('Not authenticated.'));
      }

      return api.accounts.update({
        id: user.id,
        ...params,
      });
    },
    {
      ...options,
      onSuccess: (account, ...args) => {
        queryClient.setQueryData(['account', account.id], account);

        if (onSuccess) {
          onSuccess(account, ...args);
        }
      },
      onSettled: (...args) => {
        if (onSettled) {
          onSettled(...args);
        }

        queryClient.invalidateQueries(['account']);
      },
    }
  );
}

export function useUpdateAccountPassword(
  options: UseMutationOptions<Account, APIError, UpdatePasswordParams> = {}
) {
  const api = useAPI();
  const { user } = useAuthentication();

  return useMutation((params) => {
    if (!user) {
      return Promise.reject(new Error('Not authenticated.'));
    }

    return api.accounts.updatePassword(user.id, params);
  }, options);
}

export function useSetDefaultDashboard(
  options: UseMutationOptions<
    Account,
    APIError,
    { applicationId: string; dashboardId?: string }
  > = {}
) {
  const api = useAPI();
  const queryClient = useAppQueryClient();
  const { user } = useAuthentication();

  return useMutation(
    ({ applicationId, dashboardId }) => {
      if (!user) {
        return Promise.reject(new Error('Not authenticated.'));
      }

      const currentSerialized = user.account_attributes?.default_dashboards;
      const updatedSerialized = setDefaultDashboard(
        currentSerialized,
        applicationId,
        dashboardId
      );

      return api.accounts.update({
        id: user.id,
        account_attributes: {
          ...user.account_attributes,
          default_dashboards: updatedSerialized,
        },
      });
    },
    {
      ...options,
      onSuccess: (account, ...args) => {
        queryClient.setQueryData(['account', account.id], account);

        if (options.onSuccess) {
          options.onSuccess(account, ...args);
        }
      },
      onSettled: (...args) => {
        if (options.onSettled) {
          options.onSettled(...args);
        }

        queryClient.invalidateQueries(['account']);
      },
    }
  );
}
